import chartsBase from './chats-base.js';

/**
 * CONFIGURAÇÕES DOS GRÁFICOS DO TUTORIAL
 *
 * Este arquivo contém os componentes e seus dados.
 * Ele NÃO monta fieldsets. A posição dos blocos na aba fica em
 * charts-layout.js.
 *
 * Esta aba ensina COMO configurar cada componente e funciona como catálogo
 * visual do que já existe. A composição real dos componentes foi separada para
 * tutorial/dashboard-layout.js, onde a malha canônica é exercitada como um
 * Dashboard de verdade.
 *
 * Os gráficos tradicionais continuam definidos de forma extremamente verbosa
 * em chats-base.js. Isso evita reescrever dezenas de configurações já corretas
 * neste primeiro passo da reorganização.
 */

/**
 * MINIDISPLAY
 *
 * `label` é o título fixo do componente.
 * `metrics` aceita de 1 a 5 indicadores.
 * Cada item de `metrics` possui:
 * - subtitle: identificação daquela métrica;
 * - value: valor exibido;
 * - unit: unidade;
 * - scale: limites usados para definir a cor semântica.
 */
export const miniDisplays = [
  {
    element: 'miniDisplay',
    label: 'Pipeline de Leads',
    metrics: [
      {
        subtitle: 'Total',
        value: 256,
        unit: 'Leads',
        scale: { green: 0 }
      }
    ]
  },
  {
    element: 'miniDisplay',
    label: 'Deploy Frequency',
    metrics: [
      {
        subtitle: 'Diário',
        value: 3,
        unit: 'Deploys',
        scale: { red: 0, yellow: 2, green: 3 }
      },
      {
        subtitle: 'Semanal (Média)',
        value: 14,
        unit: 'Deploys',
        scale: { red: 0, yellow: 8, green: 12 }
      },
      {
        subtitle: 'Mensal',
        value: 35,
        unit: 'Deploys',
        scale: { red: 0, yellow: 20, green: 30 }
      }
    ]
  },
  {
    element: 'miniDisplay',
    label: 'Leads por Situação',
    metrics: [
      { subtitle: 'Prospectar', value: 20, unit: 'Leads', scale: { green: 0 } },
      { subtitle: 'Aguardar', value: 60, unit: 'Leads', scale: { yellow: 0 } },
      { subtitle: 'Descartar', value: 45, unit: 'Leads', scale: { red: 0 } },
      { subtitle: 'Convertidos', value: 15, unit: 'Leads', scale: { green: 0 } }
    ]
  },
  {
    element: 'miniDisplay',
    label: 'SLA de Atendimento',
    metrics: [
      {
        subtitle: '1ª resposta',
        value: 18,
        unit: 'h',
        scale: { green: 0, yellow: 8, red: 16 }
      },
      {
        subtitle: 'Resolução',
        value: 72,
        unit: 'h',
        scale: { green: 0, yellow: 24, red: 48 }
      }
    ]
  },
  {
    element: 'miniDisplay',
    label: 'Dados de Conversão',
    metrics: [
      { subtitle: 'Semana atual', value: null, unit: '%' },
      { subtitle: 'Média mensal', value: null, unit: '%' }
    ]
  }
];

export const metricDisplays = [
  {
    element: 'metricDisplay',
    label: 'dic.metricDisplayTutorial.emailContactsTitle',
    metrics: [
      {
        subtitle: 'dic.labels.total',
        value: 1026,
      },
      {
        subtitle: 'dic.metricDisplayTutorial.changed',
        value: 34,
        unit: '%',
        scale: { red: -100, common: 0, green: 0.01 },
        icons: {
          red: { type: 'solid', name: 'arrow-trend-down' },
          common: { type: 'solid', name: 'minus' },
          green: { type: 'solid', name: 'arrow-trend-up' },
        },
      },
      {
        subtitle: 'dic.metricDisplayTutorial.previousMonth',
        value: 767,
        icon: { type: 'regular', name: 'calendar' },
      },
    ],
  },

  /**
   * VÁRIOS PAINÉIS
   *
   * `panels` aceita quantos painéis forem necessários. Todo painel tem sempre
   * 3 métricas: o número grande, a variação e a referência anterior.
   * Para um indicador com uma única métrica use miniDisplay.
   *
   * A faixa lateral é comandada somente pelos números grandes:
   * todos verdes = verde, todos vermelhos = vermelho, qualquer outra
   * combinação = amarelo e sem dados = cinza.
   */
  {
    element: 'metricDisplay',
    label: 'dic.metricDisplayTutorial.deployTitle',
    panels: [
      {
        metrics: [
          {
            subtitle: 'dic.metricDisplayTutorial.daily',
            value: 3,
            scale: { red: 0, yellow: 2, green: 3 },
          },
          {
            subtitle: 'dic.metricDisplayTutorial.changed',
            value: 50,
            unit: '%',
            scale: { red: -100, common: 0, green: 0.01 },
            icons: {
              red: { type: 'solid', name: 'arrow-trend-down' },
              common: { type: 'solid', name: 'minus' },
              green: { type: 'solid', name: 'arrow-trend-up' },
            },
          },
          {
            subtitle: 'dic.metricDisplayTutorial.previousDay',
            value: 2,
            icon: { type: 'regular', name: 'calendar' },
          },
        ],
      },
      {
        metrics: [
          {
            subtitle: 'dic.metricDisplayTutorial.weekly',
            value: 14,
            scale: { red: 0, yellow: 10, green: 14 },
          },
          {
            subtitle: 'dic.metricDisplayTutorial.changed',
            value: 12,
            unit: '%',
            scale: { red: -100, common: 0, green: 0.01 },
            icons: {
              red: { type: 'solid', name: 'arrow-trend-down' },
              common: { type: 'solid', name: 'minus' },
              green: { type: 'solid', name: 'arrow-trend-up' },
            },
          },
          {
            subtitle: 'dic.metricDisplayTutorial.previousWeek',
            value: 12,
            icon: { type: 'regular', name: 'calendar' },
          },
        ],
      },
      {
        metrics: [
          {
            subtitle: 'dic.metricDisplayTutorial.monthly',
            value: 35,
            scale: { red: 0, yellow: 20, green: 35 },
          },
          {
            subtitle: 'dic.metricDisplayTutorial.changed',
            value: -8,
            unit: '%',
            scale: { red: -100, common: 0, green: 0.01 },
            icons: {
              red: { type: 'solid', name: 'arrow-trend-down' },
              common: { type: 'solid', name: 'minus' },
              green: { type: 'solid', name: 'arrow-trend-up' },
            },
          },
          {
            subtitle: 'dic.metricDisplayTutorial.previousMonth',
            value: 38,
            icon: { type: 'regular', name: 'calendar' },
          },
        ],
      },
    ],
  },
];

/**
 * GAUGE DE ARCO
 *
 * `type: 'gauge'` sem variant usa o visual semicircular que já foi aprovado.
 *
 * `plotOptions.fast` define a escala do instrumento.
 * `series` aceita de 1 a 5 indicadores.
 * O `subtitle` de cada série aparece no topo do respectivo slot.
 */
export const gauge = {
  element: 'chart',
  type: 'gauge',
  title: 'Cycle Time',
  plotOptions: {
    fast: {
      min: 0,
      max: 200,
      tick: 50,
      tickDivisor: 10,
      colors: {
        green: 0,
        yellowgreen: 65,
        yellow: 120,
        orange: 150,
        red: 180,
        black: false
      }
    }
  },
  series: [
    {
      subtitle: 'Diário',
      name: 'CycleTimeDaily',
      unit: 'h',
      value: 90,
      options: { dataLabels: { format: '{value}', fixed: 0 } }
    },
    {
      subtitle: 'Semanal',
      name: 'CycleTimeWeekly',
      unit: 'h',
      value: 125,
      options: { dataLabels: { format: '{value}', fixed: 0 } }
    },
    {
      subtitle: 'Mensal',
      name: 'CycleTimeMonthly',
      unit: 'h',
      value: 165,
      options: { dataLabels: { format: '{value}', fixed: 0 } }
    }
  ]
};

/**
 * SPEED
 *
 * Continua usando o tipo legado `speed` enquanto refinamos essa representação
 * individualmente. A migração para `type: 'gauge', variant: 'speed'` será feita
 * quando a nova API da família estiver fechada.
 */
export const speed = {
  element: 'chart',
  type: 'speed',
  title: 'Deploy Frequency',
  plotOptions: {
    fast: {
      min: 0,
      max: 50,
      tick: 10,
      tickDivisor: 5,
      colors: {
        red: 0,
        yellow: 10,
        yellowgreen: 20,
        green: 30,
        black: false
      }
    }
  },
  series: [
    {
      subtitle: 'Diário',
      name: 'DeployDaily',
      unit: 'deploys',
      value: 12,
      options: { dataLabels: { format: '{value}', fixed: 0 } }
    },
    {
      subtitle: 'Semanal',
      name: 'DeployWeekly',
      unit: 'deploys',
      value: 27,
      options: { dataLabels: { format: '{value}', fixed: 0 } }
    },
    {
      subtitle: 'Mensal',
      name: 'DeployMonthly',
      unit: 'deploys',
      value: 42,
      options: { dataLabels: { format: '{value}', fixed: 0 } }
    }
  ]
};

/**
 * VU
 *
 * Também continua no tipo legado durante esta etapa. O objetivo é alterar uma
 * representação por vez para não quebrar três instrumentos simultaneamente.
 */
export const vu = {
  element: 'chart',
  type: 'vu',
  title: 'Utilização',
  plotOptions: {
    fast: {
      min: 0,
      max: 100,
      tick: 25,
      tickDivisor: 5,
      colors: {
        green: 0,
        yellowgreen: 55,
        yellow: 70,
        orange: 82,
        red: 92,
        black: false
      }
    }
  },
  series: [
    {
      subtitle: 'Diário',
      name: 'UtilizationDaily',
      unit: '%',
      value: 48,
      options: { dataLabels: { format: '{value}', fixed: 0 } }
    },
    {
      subtitle: 'Semanal',
      name: 'UtilizationWeekly',
      unit: '%',
      value: 73,
      options: { dataLabels: { format: '{value}', fixed: 0 } }
    },
    {
      subtitle: 'Mensal',
      name: 'UtilizationMonthly',
      unit: '%',
      value: 91,
      options: { dataLabels: { format: '{value}', fixed: 0 } }
    }
  ]
};

/**
 * GAUGE LINEAR
 *
 * Esta é a substituição do antigo bullet visual.
 *
 * A API nova usa:
 *
 *   type: 'gauge'
 *   variant: 'linear'
 *
 * Assim o consumidor continua trabalhando com a mesma família de instrumento
 * e muda somente a representação visual.
 *
 * Estrutura do indicador linear:
 * - faixa qualitativa larga ao fundo;
 * - barra fina e sólida representando o valor atual;
 * - linha vertical de `target` representando a meta;
 * - escala legível na base, com fonte mínima de 9px;
 * - valor e unidade acima da faixa.
 *
 * O antigo `type: 'bullet'` ainda é aceito pelo componente como alias de
 * compatibilidade, mas novos exemplos devem usar gauge + variant linear.
 */
export const linear = {
  element: 'chart',
  type: 'gauge',
  variant: 'linear',
  title: 'Cycle Time',

  plotOptions: {
    fast: {
      min: 0,
      max: 200,
      tick: 50,

      // As mesmas faixas semânticas usadas no gauge de arco podem ser usadas
      // no linear. Aqui elas formam as zonas qualitativas da barra de fundo.
      colors: {
        green: 0,
        yellowgreen: 65,
        yellow: 120,
        orange: 150,
        red: 180,
        black: false
      }
    }
  },

  series: [
    {
      subtitle: 'Diário',
      name: 'CycleTimeDailyLinear',
      unit: 'h',
      value: 90,

      // `target` é opcional. Quando existe, vira a linha vertical de referência.
      target: 110,

      options: {
        dataLabels: {
          format: '{value}',
          fixed: 0
        }
      }
    },
    {
      subtitle: 'Semanal',
      name: 'CycleTimeWeeklyLinear',
      unit: 'h',
      value: 125,
      target: 140,
      options: {
        dataLabels: {
          format: '{value}',
          fixed: 0
        }
      }
    },
    {
      subtitle: 'Mensal',
      name: 'CycleTimeMonthlyLinear',
      unit: 'h',
      value: 165,
      target: 175,
      options: {
        dataLabels: {
          format: '{value}',
          fixed: 0
        }
      }
    }
  ]
};

/**
 * Alias exportado somente para código antigo do tutorial que ainda importe
 * `bullet`. O objeto já usa a API nova; portanto não existe mais um segundo
 * exemplo visual concorrente.
 */
export const bullet = linear;

/**
 * CATÁLOGO EXECUTÁVEL DA FAMÍLIA GAUGE
 *
 * Este bloco existe por um motivo diferente dos exemplos de dashboard acima.
 *
 * O dashboard mostra COMO os componentes podem ser usados em contexto.
 * O catálogo mostra O QUE existe na biblioteca neste momento.
 *
 * Regra para manutenção futura:
 * sempre que uma nova representação de gauge for implementada, ela deve ganhar
 * um exemplo visível neste array. Não deixe uma variant, skin ou representação
 * relevante existir somente no código.
 *
 * Cada item abaixo usa UMA única métrica de propósito. Isso mantém a vitrine
 * compacta e permite colocar as quatro representações lado a lado.
 *
 * O catálogo contém apenas implementações reais. Não usamos placeholders para
 * variantes planejadas, porque a função desta seção é afirmar: "isto existe e
 * pode ser usado agora".
 */

/**
 * ARC
 *
 * Gauge semicircular sem ponteiro. É o `type: 'gauge'` padrão.
 */
export const gaugeArcCatalog = {
  element: 'chart',
  type: 'gauge',
  title: 'Gauge · Arc',
  plotOptions: {
    fast: {
      min: 0,
      max: 100,
      tick: 25,
      colors: {
        green: 0,
        yellowgreen: 50,
        yellow: 70,
        orange: 85,
        red: 95,
        black: false
      }
    }
  },
  series: [
    {
      subtitle: 'Atual',
      name: 'GaugeArcCatalog',
      unit: '%',
      value: 64,
      options: {
        dataLabels: {
          format: '{value}',
          fixed: 0
        }
      }
    }
  ]
};

/**
 * SPEED
 *
 * Gauge semicircular com ponteiro e escala mais próxima de um velocímetro.
 */
export const gaugeSpeedCatalog = {
  element: 'chart',
  type: 'speed',
  title: 'Gauge · Speed',
  plotOptions: {
    fast: {
      min: 0,
      max: 100,
      tick: 20,
      tickDivisor: 5,
      colors: {
        green: 0,
        yellowgreen: 50,
        yellow: 70,
        orange: 85,
        red: 95,
        black: false
      }
    }
  },
  series: [
    {
      subtitle: 'Atual',
      name: 'GaugeSpeedCatalog',
      unit: '%',
      value: 64,
      options: {
        dataLabels: {
          format: '{value}',
          fixed: 0
        }
      }
    }
  ]
};

/**
 * VU
 *
 * Medidor arqueado inspirado em VU meter. Continua usando o renderer `vu`
 * enquanto a API pública da família gauge é consolidada.
 */
export const gaugeVuCatalog = {
  element: 'chart',
  type: 'vu',
  title: 'Gauge · VU',
  plotOptions: {
    fast: {
      min: 0,
      max: 100,
      tick: 25,
      tickDivisor: 5,
      colors: {
        green: 0,
        yellowgreen: 50,
        yellow: 70,
        orange: 85,
        red: 95,
        black: false
      }
    }
  },
  series: [
    {
      subtitle: 'Atual',
      name: 'GaugeVuCatalog',
      unit: '%',
      value: 64,
      options: {
        dataLabels: {
          format: '{value}',
          fixed: 0
        }
      }
    }
  ]
};

/**
 * LINEAR
 *
 * Gauge linear que substitui o antigo bullet visual. `target` mostra uma meta
 * independente do valor atual.
 */
export const gaugeLinearCatalog = {
  element: 'chart',
  type: 'gauge',
  variant: 'linear',
  title: 'Gauge · Linear',
  plotOptions: {
    fast: {
      min: 0,
      max: 100,
      tick: 25,
      colors: {
        green: 0,
        yellowgreen: 50,
        yellow: 70,
        orange: 85,
        red: 95,
        black: false
      }
    }
  },
  series: [
    {
      subtitle: 'Atual',
      name: 'GaugeLinearCatalog',
      unit: '%',
      value: 64,
      target: 75,
      options: {
        dataLabels: {
          format: '{value}',
          fixed: 0
        }
      }
    }
  ]
};

/**
 * Array único usado pelo layout para montar a vitrine da família Gauge.
 *
 * A ordem é propositalmente estável. Novas representações devem ser adicionadas
 * no final ou reposicionadas conscientemente, nunca escondidas em outro arquivo.
 */
export const gaugeCatalog = [
  gaugeArcCatalog,
  gaugeSpeedCatalog,
  gaugeVuCatalog,
  gaugeLinearCatalog
];

/**
 * GRÁFICOS TRADICIONAIS
 *
 * line, spline, area, stacked percentage area, bar, column, pie, bubble, radar
 * e spider continuam integralmente descritos em chats-base.js.
 */
export const traditionalCharts = chartsBase.groups[1].content;

/**
 * QUICK CHARTS exibidos no dashboard.
 *
 * A ordem continua deliberadamente explícita para facilitar manutenção do
 * tutorial: arco, speed, VU e linear.
 */
export const quickCharts = [
  gauge,
  speed,
  vu,
  linear
];
